import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import Table from "../../../core/common/dataTable/index"
import {
  templatesApi,
  masterTemplatesApi,
  CERT_TYPES,
  type CertTemplate,
  type CertMasterTemplate,
} from "../../../service/certificate"
import { getAllClasses } from "../../../service/classes"
import { all_routes } from "../../router/all_routes"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"

const typeLabel = (v: string) => CERT_TYPES.find(t => t.value === v)?.label || v

const DEFAULT_DEFINITION = {
  canvas: { width: 1152, height: 960, background: "#ffffff" },
  a4_grid: null,
  layers: [],
}

// ── Main page ────────────────────────────────────────────────────────────────
const CertTemplates = () => {
  const routes = all_routes
  const navigate = useNavigate()
  const currentBranch = useSelector(selectCurrentBranch)

  const [items, setItems] = useState<CertTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState("")
  const [showInactive, setShowInactive] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Modal state
  const [showNew, setShowNew] = useState(false)
  const [renaming, setRenaming] = useState<CertTemplate | null>(null)
  const [classesEditing, setClassesEditing] = useState<CertTemplate | null>(null)
  const [masterEditing, setMasterEditing] = useState<CertTemplate | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const fetchItems = async () => {
    setLoading(true)
    try {
      const params: Record<string, any> = {}
      if (showInactive) params.includeInactive = 1
      if (typeFilter) params.certificateType = typeFilter
      if (currentBranch?._id) params.branch = currentBranch._id
      const res = await templatesApi.list(params)
      if (res.success) setItems(res.data)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load templates")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchItems() }, [showInactive, typeFilter, currentBranch?._id]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1) }, [items.length, search])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(t => t.name.toLowerCase().includes(q))
  }, [items, search])

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await templatesApi.remove(deleteId)
      toast.success("Template deleted")
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed")
    } finally {
      setDeleteId(null)
    }
  }

  const handleRestore = async (id: string) => {
    try {
      await templatesApi.restore(id)
      toast.success("Restored")
      fetchItems()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Restore failed")
    }
  }

  const handleDuplicate = async (record: CertTemplate) => {
    const name = window.prompt("Name for the copy:", `${record.name} (Copy)`)
    if (!name) return
    try {
      const res = await templatesApi.duplicate(record._id, name)
      toast.success("Duplicated")
      fetchItems()
      navigate(routes.certDesignerEdit.replace(":id", res.data._id))
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Duplicate failed")
    }
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (v: string, r: CertTemplate) => (
        <Link to={routes.certDesignerEdit.replace(":id", r._id)} className="link-primary fw-medium">
          {v}
        </Link>
      ),
    },
    {
      title: "Type",
      dataIndex: "certificateType",
      render: (v: string) => <span className="badge bg-primary-transparent">{typeLabel(v)}</span>,
    },
    {
      title: "Master",
      dataIndex: "masterTemplate",
      render: (m: CertTemplate["masterTemplate"]) =>
        m ? (
          <span className="badge bg-light text-dark border">{m.name}</span>
        ) : (
          <span className="text-muted small">Standalone</span>
        ),
    },
    {
      title: "Classes",
      dataIndex: "classes",
      render: (cls: CertTemplate["classes"]) => {
        if (!cls?.length) return <span className="badge bg-warning-transparent text-warning">All (fallback)</span>
        const head = cls.slice(0, 3)
        const tail = cls.length - head.length
        return (
          <div className="d-flex flex-wrap gap-1">
            {head.map(c => <span key={c._id} className="badge bg-light text-dark border">{c.name}</span>)}
            {tail > 0 && <span className="badge bg-secondary-transparent">+{tail}</span>}
          </div>
        )
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (v: boolean) =>
        v === false ? (
          <span className="badge bg-secondary-transparent text-secondary">Inactive</span>
        ) : (
          <span className="badge bg-success-transparent text-success">Active</span>
        ),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      render: (v: string) => v ? new Date(v).toLocaleDateString() : "—",
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: CertTemplate) =>
        record.isActive === false ? (
          <div className="d-flex align-items-center gap-1">
            <Link to={routes.certDesignerEdit.replace(":id", record._id)} className="btn btn-sm btn-primary" title="Design">
              <i className="ti ti-edit" />
            </Link>
            <button className="btn btn-sm btn-outline-success" onClick={() => handleRestore(record._id)} title="Restore">
              <i className="ti ti-restore" />
            </button>
          </div>
        ) : (
          <div className="d-flex align-items-center gap-1 flex-wrap">
            <Link to={routes.certDesignerEdit.replace(":id", record._id)} className="btn btn-sm btn-primary" title="Open Designer">
              <i className="ti ti-edit me-1" />Design
            </Link>
            <button className="btn btn-sm btn-outline-secondary" title="Rename" onClick={() => setRenaming(record)}>
              <i className="ti ti-forms" />
            </button>
            <button className="btn btn-sm btn-outline-secondary" title="Manage classes" onClick={() => setClassesEditing(record)}>
              <i className="ti ti-school" />
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              title={record.masterTemplate ? `Change master (${record.masterTemplate.name})` : "Link to master template"}
              onClick={() => setMasterEditing(record)}
            >
              <i className="ti ti-link" />
            </button>
            <button className="btn btn-sm btn-outline-secondary" title="Duplicate" onClick={() => handleDuplicate(record)}>
              <i className="ti ti-copy" />
            </button>
            <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => setDeleteId(record._id)}>
              <i className="ti ti-trash" />
            </button>
          </div>
        ),
    },
  ]

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Templates</h3>
            <p className="text-muted mb-0">
              Saved certificate designs. Each template targets one certificate type and optionally specific classes.
            </p>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <input
              className="form-control"
              style={{ maxWidth: 200 }}
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select
              className="form-select"
              style={{ maxWidth: 200 }}
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
            >
              <option value="">All types</option>
              {CERT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <div className="form-check form-switch mb-0 me-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="cert_tpl_inactive"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="cert_tpl_inactive">Show inactive</label>
            </div>
            <button className="btn btn-primary" onClick={() => setShowNew(true)}>
              <i className="ti ti-square-rounded-plus me-2" />New Template
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0 py-3">
            <Table
              dataSource={visible}
              columns={columns}
              loading={loading}
              pagination={{
                currentPage: page,
                limit: pageSize,
                total: visible.length,
                onChange: (p: number, ps: number) => { setPage(p); setPageSize(ps) },
              }}
            />
          </div>
        </div>
      </div>

      {showNew && (
        <NewTemplateModal
          onClose={() => setShowNew(false)}
          onSaved={(id) => { setShowNew(false); fetchItems(); navigate(routes.certDesignerEdit.replace(":id", id)) }}
        />
      )}

      {renaming && (
        <RenameModal
          template={renaming}
          onClose={() => setRenaming(null)}
          onSaved={() => { setRenaming(null); fetchItems() }}
        />
      )}

      {classesEditing && (
        <ClassesModal
          template={classesEditing}
          onClose={() => setClassesEditing(null)}
          onSaved={() => { setClassesEditing(null); fetchItems() }}
        />
      )}

      {masterEditing && (
        <ChangeMasterModal
          template={masterEditing}
          onClose={() => setMasterEditing(null)}
          onSaved={() => { setMasterEditing(null); fetchItems() }}
        />
      )}

      {deleteId && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center p-4">
                  <span className="text-danger" style={{ fontSize: 40 }}><i className="ti ti-trash-x" /></span>
                  <h4 className="mt-2">Delete Template</h4>
                  <p className="text-muted">Soft-deletes — past certificates generated from it stay valid. Can be restored.</p>
                  <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-light" onClick={() => setDeleteId(null)}>Cancel</button>
                    <button className="btn btn-danger" onClick={handleDelete}>Yes, Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  )
}

// ── New Template modal ────────────────────────────────────────────────────────
const NewTemplateModal = ({ onClose, onSaved }: { onClose: () => void; onSaved: (id: string) => void }) => {
  const [name, setName] = useState("")
  const [certType, setCertType] = useState("class_result")
  const [masterId, setMasterId] = useState("")
  const [active, setActive] = useState(true)
  const [masters, setMasters] = useState<CertMasterTemplate[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    masterTemplatesApi.list().then(r => { if (r.success) setMasters(r.data) }).catch(() => {})
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) { setError("Name is required"); return }
    if (!certType) { setError("Certificate type is required"); return }
    setSaving(true); setError(null)
    try {
      const payload: any = {
        name: name.trim(),
        certificateType: certType,
        definition: DEFAULT_DEFINITION,
        isActive: active,
      }
      if (masterId) payload.masterTemplate = masterId
      const res = await templatesApi.create(payload)
      toast.success("Template created")
      onSaved(res.data._id)
    } catch (err: any) {
      setError(err?.response?.data?.message || "Create failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">New template</h4>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name *</label>
                  <input type="text" className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Winner 1-12" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Certificate type *</label>
                  <select className="form-select" value={certType} onChange={e => setCertType(e.target.value)}>
                    {CERT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <small className="text-muted">Drives which dynamic fields the designer exposes. Changeable later but may invalidate type-specific layers.</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Master template</label>
                  <select className="form-select" value={masterId} onChange={e => setMasterId(e.target.value)}>
                    <option value="">— None (blank template) —</option>
                    {masters.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
                  </select>
                  <small className="text-muted">Optional. Linked templates inherit the master's layers; editing the master updates every linked template live.</small>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="new_tpl_active" checked={active} onChange={e => setActive(e.target.checked)} />
                  <label className="form-check-label" htmlFor="new_tpl_active">Active</label>
                </div>
                {error && <div className="alert alert-danger mt-3 mb-0 py-2 px-3 small">{error}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Creating…" : "Create and open designer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

// ── Rename modal ──────────────────────────────────────────────────────────────
const RenameModal = ({ template, onClose, onSaved }: { template: CertTemplate; onClose: () => void; onSaved: () => void }) => {
  const [name, setName] = useState(template.name)
  const [saving, setSaving] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    try {
      await templatesApi.update(template._id, { name: name.trim() })
      toast.success("Renamed")
      onSaved()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Rename failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Rename template</h4>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <label className="form-label">Name *</label>
                <input type="text" className="form-control" value={name} onChange={e => setName(e.target.value)} autoFocus />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving || !name.trim()}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

// ── Change master modal ────────────────────────────────────────────────────────
const ChangeMasterModal = ({ template, onClose, onSaved }: { template: CertTemplate; onClose: () => void; onSaved: () => void }) => {
  const [selectedId, setSelectedId] = useState(template.masterTemplate?._id || "")
  const [masters, setMasters] = useState<CertMasterTemplate[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    masterTemplatesApi.list().then(r => { if (r.success) setMasters(r.data) }).catch(() => {})
  }, [])

  const save = async () => {
    setSaving(true)
    try {
      await templatesApi.update(template._id, { masterTemplate: selectedId || null })
      toast.success(selectedId ? "Master updated" : "Master unlinked")
      onSaved()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const unlinking = !selectedId && !!template.masterTemplate
  const switching = selectedId && selectedId !== template.masterTemplate?._id

  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Master template — {template.name}</h4>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <label className="form-label">Master template</label>
              <select className="form-select mb-2" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
                <option value="">— None (standalone) —</option>
                {masters.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
              </select>
              <small className="text-muted">Swap which master design this template inherits from, or pick None to detach.</small>
              {unlinking && (
                <div className="alert alert-warning mt-3 mb-0 py-2 px-3 small">
                  Unlinking will stop rendering the current master's layers. Only your own-added layers will remain on generated certificates.
                </div>
              )}
              {switching && (
                <div className="alert alert-info mt-3 mb-0 py-2 px-3 small">
                  The new master's canvas dimensions are NOT reseeded — open the Designer if the layout looks off after switching.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

// ── Classes modal ─────────────────────────────────────────────────────────────
const ClassesModal = ({ template, onClose, onSaved }: { template: CertTemplate; onClose: () => void; onSaved: () => void }) => {
  const [allClasses, setAllClasses] = useState<{ _id: string; name: string }[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set(template.classes?.map(c => c._id) || []))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getAllClasses({ limit: 500 }).then((r: any) => {
      const list = r?.data?.data || r?.data || []
      setAllClasses(list.map((c: any) => ({ _id: c._id, name: c.name })))
    }).catch(() => {})
  }, [])

  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const save = async () => {
    setSaving(true)
    try {
      await templatesApi.setClasses(template._id, Array.from(selected))
      toast.success(`Classes saved (${selected.size})`)
      onSaved()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Map classes — {template.name}</h4>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-3">
                Pick which classes should use this template at generation time. Leave empty to use as fallback when no class-specific template matches.
              </p>
              <div className="d-flex gap-2 mb-3">
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelected(new Set(allClasses.map(c => c._id)))}>
                  Select all
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelected(new Set())}>
                  Clear
                </button>
                <span className="text-muted small align-self-center">{selected.size} selected</span>
              </div>
              {allClasses.length === 0 ? (
                <p className="text-muted small">No classes found.</p>
              ) : (
                <div className="row g-2">
                  {allClasses.map(c => (
                    <div className="col-6 col-md-4" key={c._id}>
                      <label className="d-flex align-items-center gap-2 border rounded px-2 py-1 cursor-pointer" style={{ cursor: "pointer" }}>
                        <input type="checkbox" checked={selected.has(c._id)} onChange={() => toggle(c._id)} />
                        <span className="small">{c.name}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? "Saving…" : `Save (${selected.size} class${selected.size === 1 ? "" : "es"})`}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

export default CertTemplates
